/**
 * useDecisions — polls the unified decisions endpoint and manages local state.
 *
 * Polls GET /api/admin/ops/decisions/unified every 30 s with current filters
 * as query params.
 *
 * Returns:
 *   decisions        — merged platform + legal list
 *   totalPlatform    — raw platform count from last fetch
 *   totalLegal       — raw legal count from last fetch
 *   loading          — true on the first in-flight fetch only
 *   error            — last axios error, or null
 *   refetch          — call to force an immediate refresh
 *
 * Actions (all return promises, reject on network failure):
 *   decideAction(decisionId, payload)     → POST /api/admin/ops/decisions/:id/decide
 *   snoozeAction(decisionId, minutes)     → POST /api/admin/ops/decisions/:id/snooze
 *   legalDecideAction(ticketId, payload)  → POST /api/admin/ops/legal/tickets/:id/decide
 *   undoAction(decisionId)               → POST /api/admin/ops/decisions/:id/undo
 *
 * Optimistic behaviour:
 *   decide / snooze / legalDecide remove the item from the list immediately.
 *   undo triggers a full refetch instead (the row re-appears).
 */
import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { getAdminToken } from "@/lib/authStorage";

const POLL_INTERVAL_MS = 30_000;

function authHeaders() {
  const token = getAdminToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Build URLSearchParams from a filters object, skipping falsy values.
 * @param {object} filters
 * @returns {string} query string (without leading "?")
 */
function buildQuery(filters = {}) {
  const params = new URLSearchParams();

  if (filters.source)        params.set("source",          filters.source);
  if (filters.stakes)        params.set("stakes",          filters.stakes);
  if (filters.urgency)       params.set("urgency",         filters.urgency);
  if (filters.agentId)       params.set("agent_id",        filters.agentId);
  if (filters.sort)          params.set("sort",            filters.sort);
  if (filters.includeDecided) params.set("include_decided", "true");

  return params.toString();
}

/**
 * @param {object} filters
 * @param {string} [filters.source]         "all" | "platform" | "legal"
 * @param {string} [filters.stakes]         "high" | "medium" | "low"
 * @param {string} [filters.urgency]        "urgent" | "normal" | "low"
 * @param {string} [filters.agentId]
 * @param {string} [filters.sort]
 * @param {boolean} [filters.includeDecided]
 */
export function useDecisions(filters = {}) {
  const [decisions, setDecisions]         = useState([]);
  const [totalPlatform, setTotalPlatform] = useState(0);
  const [totalLegal, setTotalLegal]       = useState(0);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);

  // Keep a stable ref to the current filters so the interval callback
  // always reads the latest value without needing to restart the interval.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const intervalRef = useRef(null);

  // ── Core fetch ──────────────────────────────────────────────────────────────

  const fetchDecisions = useCallback(async () => {
    try {
      const qs = buildQuery(filtersRef.current);
      const url = `${API}/admin/ops/decisions/unified${qs ? `?${qs}` : ""}`;
      const res = await axios.get(url, { headers: authHeaders() });

      setDecisions(res.data?.decisions   || []);
      setTotalPlatform(res.data?.total_platform ?? 0);
      setTotalLegal(res.data?.total_legal    ?? 0);
      setError(null);
    } catch (err) {
      setError(err);
      // Retain stale data — do NOT clear the decisions array.
    } finally {
      // Only relevant on first load; subsequent polls silently update state.
      setLoading(false);
    }
  }, []); // stable — reads filtersRef inside

  // ── Polling setup + teardown ────────────────────────────────────────────────

  useEffect(() => {
    // Restart polling + reset loading state whenever filters change.
    setLoading(true);
    fetchDecisions();

    intervalRef.current = setInterval(fetchDecisions, POLL_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [fetchDecisions, JSON.stringify(filters)]); // eslint-disable-line react-hooks/exhaustive-deps
  // JSON.stringify is intentional: plain objects aren't referentially stable
  // between renders when built inline by the parent.

  // ── Optimistic helpers ──────────────────────────────────────────────────────

  function optimisticRemove(id) {
    setDecisions((prev) => prev.filter((d) => d.id !== id));
  }

  // ── Actions ─────────────────────────────────────────────────────────────────

  /**
   * POST /api/admin/ops/decisions/:id/decide
   * payload: { decision, notes?, founder_choice? }
   */
  const decideAction = useCallback(async (decisionId, payload) => {
    const res = await axios.post(
      `${API}/admin/ops/decisions/${decisionId}/decide`,
      payload,
      { headers: authHeaders() },
    );
    optimisticRemove(decisionId);
    return res.data;
  }, []);

  /**
   * POST /api/admin/ops/decisions/:id/snooze
   * minutes: number (1–10080)
   */
  const snoozeAction = useCallback(async (decisionId, minutes) => {
    const res = await axios.post(
      `${API}/admin/ops/decisions/${decisionId}/snooze`,
      { minutes },
      { headers: authHeaders() },
    );
    optimisticRemove(decisionId);
    return res.data;
  }, []);

  /**
   * POST /api/admin/ops/legal/tickets/:id/decide
   * payload: { action, notes? }  action = "approve" | "revise" | "reject"
   */
  const legalDecideAction = useCallback(async (ticketId, payload) => {
    const res = await axios.post(
      `${API}/admin/ops/legal/tickets/${ticketId}/decide`,
      payload,
      { headers: authHeaders() },
    );
    optimisticRemove(ticketId);
    return res.data;
  }, []);

  /**
   * POST /api/admin/ops/decisions/:id/undo
   * Refetches the full list so the restored row reappears.
   */
  const undoAction = useCallback(async (decisionId) => {
    const res = await axios.post(
      `${API}/admin/ops/decisions/${decisionId}/undo`,
      {},
      { headers: authHeaders() },
    );
    await fetchDecisions();
    return res.data;
  }, [fetchDecisions]);

  // ── Public API ──────────────────────────────────────────────────────────────

  return {
    decisions,
    totalPlatform,
    totalLegal,
    loading,
    error,
    refetch: fetchDecisions,
    decideAction,
    snoozeAction,
    legalDecideAction,
    undoAction,
  };
}
